// A team's whole character where a screen has room for it (PLAN-2026-10-24-
// faces-round-three §4): the matchup's two sides, the League page's team
// photo, Edit team and the champion's moment.
//
// A TEAM WITHOUT A FACE is drawn as its badge on a placard, at the character's
// height, so a team photo has every team in it and nobody is left out for
// having chosen an emoji.
//
// A BOT IS MARKED AS A BOT here as on its badge (B-488): the same tag at the
// lower right.

import type { CSSProperties } from 'react';
import {
  botFace,
  wearingHat,
  type FaceMood,
  type TeamBadge,
  type TeamColors,
  type WeeklyHat,
} from '@fsp/domain';
import { onColor } from '@fsp/theme';
import { FIGURE_BOX, FigureArt } from './FigureArt';
import { Icon } from './Icon';
import { useTeamHat } from './hat';
import { TeamMark } from './index';

export interface FigureTeam {
  abbreviation: string;
  colors: TeamColors;
  badge?: TeamBadge;
  /** Its id in the league on screen, for the week's hat (PLAN-2026-10-30-the-weekly-hat §7). */
  id?: string;
}

/** The face a team's character wears, or null for a team whose badge is not a face. */
export function teamFace(team: FigureTeam, on: Date = new Date()) {
  const badge = team.badge ?? null;
  if (badge?.kind === 'bot') return botFace(badge.id, on);
  if (badge?.kind === 'face') return badge.face;
  return null;
}

export function TeamFigure({
  team,
  mood = 'idle',
  height,
  stage = false,
  mirror = false,
  hat: given,
  className,
}: {
  team: FigureTeam;
  /**
   * The hat it wears, where a screen says which (a chat card drawn with the
   * hat it was posted with); absent, the week's hat it wears now, if any.
   */
  hat?: WeeklyHat | null;
  mood?: FaceMood;
  /** Its drawn height; absent, it fills its box's width at the character's shape. */
  height?: number;
  /** On its own stage in the team's colors. */
  stage?: boolean;
  /** Its pose the other way round, for the second of two facing each other (`FigureArt`). */
  mirror?: boolean;
  className?: string;
}) {
  // THE WEEK'S HAT ON ITS HEAD, in place of its own; a placard's mark wears
  // it perched (`TeamMark`).
  const worn = useTeamHat(team.id);
  const hat = given === undefined ? worn : given;
  const own = teamFace(team);
  const face = own !== null && hat !== null ? wearingHat(own, hat) : own;
  const width =
    height === undefined ? undefined : Math.round((height * FIGURE_BOX.w) / FIGURE_BOX.h);
  const bot = team.badge?.kind === 'bot';
  return (
    <span
      className={`fsp-teamfigure${className ? ` ${className}` : ''}`}
      style={height === undefined ? undefined : ({ width, height } as CSSProperties)}
      aria-hidden
    >
      {face !== null ? (
        <FigureArt
          face={face}
          mood={mood}
          team={team.colors}
          stage={stage}
          mirror={mirror}
          height={height}
        />
      ) : (
        // ITS BADGE ON A PLACARD: the badge where a head would be, on a pole.
        <span
          className={`fsp-teamfigure-placard${stage ? ' is-staged' : ''}`}
          style={
            {
              '--fsp-team': team.colors.primary,
              '--fsp-team-2': team.colors.secondary,
              '--fsp-team-on': onColor(team.colors.primary),
            } as CSSProperties
          }
        >
          <TeamMark
            team={team}
            size={width === undefined ? 40 : Math.round(width * 0.62)}
            face={mood}
            {...(given === undefined ? {} : { hat: given })}
          />
          <span className="fsp-teamfigure-pole" />
        </span>
      )}
      {bot && face !== null ? (
        <span className="fsp-teammark-bot fsp-teamfigure-bot">
          <Icon name="bot" size="xs" />
        </span>
      ) : null}
    </span>
  );
}
